import DT from 'duration-time-conversion';
import React, { createRef, memo, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import WFPlayer from 'wfplayer';
import AppConfig from '../../utils/AppConfig';
import Metronome from './Metronome';
import Timeline from './Timeline';

const Style = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    /* Remove this line that was causing issues */
    /* padding-bottom: 0.5em; */
    height: 100%; /* Take full height */
    
    /* Fix waveform container */
    .waveform {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 1;
        user-select: none;
        pointer-events: none;
        overflow: hidden;
        /* Remove this duplicate padding that was causing issues */
        /* padding-bottom: 0.5em; */
    }

    .progress {
        position: relative;
        width: 100% !important;
        height: 16px !important; /* Keep container height */
        min-height: 16px !important;
        user-select: none;
        background-color: rgba(50, 50, 50, 0.7); /* Darker background */
        overflow: visible;
        flex-shrink: 0;
        z-index: 100;
        display: flex !important; /* Change to flex for vertical centering */
        align-items: center !important; /* Center content vertically */
        margin: 0;
        cursor: pointer;
        pointer-events: auto !important;
        border-radius: 3px;
        outline: 1px solid cyan;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        transition: height 0.2s ease;
        
        /* Make buffer, bar and subtitle appear as thick lines */
        .buffer, .bar, .subtitle {
            position: absolute;
            left: 0;
            height: 4px !important; /* REDUCED HEIGHT for line appearance */
            min-height: 4px !important;
            border-radius: 2px;
            /* Center vertically */
            top: 50% !important;
            transform: translateY(-50%) !important;
        }
        
        /* On hover, make them slightly thicker */
        &:hover {
            .buffer, .bar, .subtitle span {
                height: 6px !important;
                min-height: 6px !important;
            }
        }
    }

    /* Update the buffer, bar, and subtitle styles to ensure consistent Y alignment */
    .buffer, .bar, .subtitle {
        position: absolute;
        left: 0;
        top: 0; 
        bottom: 0;
        height: 100% !important;
        min-height: 100% !important;
        border-radius: 3px;
        z-index: 5; /* Base z-index */
    }

    /* Ensure proper z-index stacking */
    .buffer {
        z-index: 6; /* Above background */
    }

    .bar {
        z-index: 7; /* Above buffer */
    }

    .subtitle{
        max-width: 100%; /* Ensure subtitles don't overflow */
        margin-top: 0 !important; /* Reset margin */
    }

    .subtitle span {
        z-index: 5; /* Below buffer and bar */
    }
`;

const Waveform = memo(
    ({ player, setWaveform, setRender }) => {
        const $waveform = createRef();

        useEffect(() => {
            const isDecodeAudioError = (value) => {
                const name = value?.name || '';
                const message = typeof value === 'string' ? value : value?.message || '';
                const stack = typeof value === 'string' ? '' : value?.stack || '';
                return (
                    /encodingerror/i.test(name) ||
                    /unable to decode audio data|decodeaudiodata|decode audio|unknown content type/i.test(message) ||
                    /unable to decode audio data|decodeaudiodata|decode audio/i.test(stack)
                );
            };

            const suppressDecodeAudioRejection = (event) => {
                const reason = event?.reason;
                if (isDecodeAudioError(reason)) {
                    event.preventDefault();
                    if (typeof event.stopImmediatePropagation === 'function') {
                        event.stopImmediatePropagation();
                    }
                }
            };

            const suppressDecodeAudioError = (event) => {
                if (isDecodeAudioError(event?.error) || isDecodeAudioError(event?.message)) {
                    event.preventDefault();
                    if (typeof event.stopImmediatePropagation === 'function') {
                        event.stopImmediatePropagation();
                    }
                }
            };

            const safeWaveformLoad = (source) => {
                try {
                    const result = waveform.load(source);
                    if (result && typeof result.catch === 'function') {
                        result.catch((err) => {
                            if (isDecodeAudioError(err)) {
                                return;
                            }
                            console.error('Waveform load failed:', err);
                        });
                    }
                } catch (err) {
                    if (isDecodeAudioError(err)) {
                        return;
                    }
                    console.error('Waveform load failed:', err);
                }
            };

            window.addEventListener('unhandledrejection', suppressDecodeAudioRejection, true);
            window.addEventListener('error', suppressDecodeAudioError, true);

            [...WFPlayer.instances].forEach((item) => item.destroy());

            const waveform = new WFPlayer({
                scrollable: true,
                useWorker: false,
                duration: 10,
                padding: 1,
                wave: true, // Ensure wave is enabled
                waveScale: 0.8,
                pixelRatio: 2,
                container: $waveform.current,
                mediaElement: player.element,
                progress: true,
                scrollbar: true,
                waveAlign: 'left',
                scrollbarColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(0, 0, 0, 0)', // Transparent background
                waveColor: 'rgba(255, 255, 255, 0.5)', // Brighter wave color for visibility
                progressColor: '#F44336', // Bright red for progress
                cursorColor: '#F44336', // Brighter cursor color
                cursor: true,
                gridColor: 'rgba(255, 255, 255, 0.2)',
                rulerColor: 'rgba(255, 255, 255, 0.6)',
                paddingColor: 'rgba(0, 0, 0, 0)',
                progressWidth: 2,
                progressMode: 'line',
            });

            setWaveform(waveform);
            waveform.on('update', setRender);

            const videoDuration = player?.element?.duration;
            const isLongVideo = Number.isFinite(videoDuration) && videoDuration > 3600;
            const isDevMode = process.env.NODE_ENV === 'development';
            const useRealWaveform = AppConfig.enableWaveforms && !isLongVideo && !isDevMode;
            
            // Force waveform to load
            if (useRealWaveform) {
                console.log("Loading waveform for video player");
                safeWaveformLoad(document.querySelector('#videoPlayer'));
            } else {
                if (isLongVideo && AppConfig.enableWaveforms) {
                    console.log('Skipping real waveform for long video (>1h) to improve editor startup');
                }
                if (isDevMode && AppConfig.enableWaveforms) {
                    console.log('Skipping real waveform in local dev to avoid decode runtime overlay');
                }
                console.log("Loading silence.mp3 for waveform");
                safeWaveformLoad('./silence.mp3');
            }
            
            // Make sure the progress indicator is visible
            waveform.options.progress = true;
            waveform.options.progressMode = 'line';
            waveform.options.progressColor = '#F44336';
            waveform.options.progressWidth = 2;
            waveform.options.cursor = true;
            waveform.options.cursorColor = '#F44336';
            
            // Ensure the time indicator spans the entire height
            const waveformElement = $waveform.current;
            if (waveformElement) {
                // Add CSS to ensure the WFPlayer progress line is visible
                const style = document.createElement('style');
                style.textContent = `
                    .wfplayer .wfplayer-progress {
                        position: absolute;
                        top: 0 !important;
                        height: 100% !important;
                        width: 2px !important;
                        background-color: #F44336 !important;
                        z-index: 9999 !important; /* Very high z-index */
                        pointer-events: none !important;
                    }

                    .wfplayer .wfplayer-cursor {
                        position: absolute;
                        top: 0 !important;
                        height: 100% !important;
                        background-color: #F44336 !important;
                        z-index: 9999 !important; /* Very high z-index */
                        pointer-events: none !important;
                    }
                    
                    .wfplayer {
                        background-color: rgba(0, 0, 0, 0.2) !important;
                        z-index: 10 !important;
                    }
                    
                    .wfplayer .wfplayer-wave {
                        opacity: 1 !important;
                        z-index: 15 !important;
                    }
                `;
                document.head.appendChild(style);
            }
            
            return () => {
                window.removeEventListener('unhandledrejection', suppressDecodeAudioRejection, true);
                window.removeEventListener('error', suppressDecodeAudioError, true);
                document.querySelectorAll('style').forEach(s => {
                    if (s.textContent.includes('.wfplayer')) {
                        s.remove();
                    }
                });
            };
        }, [player, $waveform, setWaveform, setRender]);

        return <div className="waveform" ref={$waveform} style={{ width: '100%', height: '100%' }} />;
    },
    () => true,
);

const Progress = (props) => {
    const [grabbing, setGrabbing] = useState(false);
    const [isDraggingHandle, setIsDraggingHandle] = useState(false);
    const [grabStartX, setGrabStartX] = useState(0);
    const [grabStartTime, setGrabStartTime] = useState(0);
    const [hoverTime, setHoverTime] = useState(null);
    const [hoverPosition, setHoverPosition] = useState(0);
    const progressRef = React.useRef(null);

    // Calculate progress percentage
    const progressPercent = (props.currentTime / props.player.element.duration) * 100;
    
    // Simulate buffer progress (could be tied to actual buffer if available)
    const bufferPercent = Math.min(100, progressPercent + 20);

    // Format time for the preview
    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };

    // Handle direct click on the progress bar
    const onProgressClick = useCallback(
        (event) => {
            if (event.button !== 0 || grabbing || isDraggingHandle) return;
            
            const rect = progressRef.current.getBoundingClientRect();
            const clickX = event.clientX - rect.left;
            const percentPosition = clickX / rect.width;
            
            const currentTime = percentPosition * props.player.element.duration;
            console.log(`Seeking to time - onProgressClick: ${currentTime}`);
            props.player.updateTime(currentTime);
            
            if (props.waveform) {
                props.waveform.seek(currentTime);
            }
        },
        [props, grabbing, isDraggingHandle],
    );

    // Handle mouse move for hover preview
    const onMouseMove = useCallback(
        (event) => {
            if (grabbing || isDraggingHandle) return;
            
            const rect = progressRef.current.getBoundingClientRect();
            const mouseX = event.clientX - rect.left;
            const percentPosition = mouseX / rect.width;
            
            // Calculate time based on position
            const time = percentPosition * props.player.element.duration;
            setHoverTime(time);
            setHoverPosition(mouseX);
        },
        [props.player.element.duration, grabbing, isDraggingHandle],
    );

    // Handle mouse enter/leave for hover effects
    const onMouseEnter = useCallback(() => {
        const timePreview = document.querySelector('.progress .time-preview');
        if (timePreview) timePreview.style.opacity = '1';
    }, []);

    const onMouseLeave = useCallback(() => {
        const timePreview = document.querySelector('.progress .time-preview');
        if (timePreview) timePreview.style.opacity = '0';
        setHoverTime(null);
    }, []);

    // Handle mouse down on progress bar or handle
    const onProgressMouseDown = useCallback(
        (event) => {
            if (event.button !== 0) return;
            
            // Check if we're clicking on the handle
            if (event.target.className === 'handle') {
                setIsDraggingHandle(true);
            } else {
                setGrabbing(true);
            }
            
            setGrabStartX(event.clientX);
            setGrabStartTime(props.currentTime);
            
            // Prevent text selection
            event.preventDefault();
        },
        [props.currentTime],
    );

    // Handle dragging
    const onGrabMove = useCallback(
        (event) => {
            if (!grabbing && !isDraggingHandle) return;
            
            const rect = progressRef.current.getBoundingClientRect();
            
            if (isDraggingHandle) {
                // Precise positioning with the handle
                const clickX = event.clientX - rect.left;
                const percentPosition = Math.max(0, Math.min(1, clickX / rect.width));
                const currentTime = percentPosition * props.player.element.duration;
                console.log(`Seeking to time - onGrabMove - 1: ${currentTime}`);
                props.player.updateTime(currentTime);
                
                if (props.waveform) {
                    props.waveform.seek(currentTime);
                }
            } else if (grabbing) {
                // Relative movement when grabbing the bar itself
                const deltaX = event.clientX - grabStartX;
                const deltaPercent = deltaX / rect.width;
                const deltaTime = deltaPercent * props.player.element.duration;
                
                // Calculate new time with sensitivity
                const sensitivity = 1.0;
                const newTime = Math.max(0, Math.min(
                    props.player.element.duration,
                    grabStartTime + (deltaTime * sensitivity)
                ));
                console.log(`Seeking to time - onGrabMove - 2: ${newTime}`);
                props.player.updateTime(newTime);
                
                if (props.waveform) {
                    props.waveform.seek(newTime);
                }
            }
        },
        [grabbing, isDraggingHandle, grabStartX, grabStartTime, props],
    );

    // Handle mouse up
    const onDocumentMouseUp = useCallback(() => {
        if (grabbing || isDraggingHandle) {
            setGrabbing(false);
            setIsDraggingHandle(false);
        }
    }, [grabbing, isDraggingHandle]);

    // Add event listeners
    useEffect(() => {
        document.addEventListener('mouseup', onDocumentMouseUp);
        document.addEventListener('mousemove', onGrabMove);
        
        return () => {
            document.removeEventListener('mouseup', onDocumentMouseUp);
            document.removeEventListener('mousemove', onGrabMove);
        };
    }, [onDocumentMouseUp, onGrabMove]);
    
    // Add useEffect to ensure dimensions are set
    useEffect(() => {
        if (progressRef.current) {
            // Log dimensions to debug
            /*console.log('Progress dimensions:', {
                offsetWidth: progressRef.current.offsetWidth,
                offsetHeight: progressRef.current.offsetHeight,
                clientWidth: progressRef.current.clientWidth,
                clientHeight: progressRef.current.clientHeight
            });*/
            
            // Force dimensions if they're zero
            if (progressRef.current.offsetWidth === 0 || progressRef.current.offsetHeight === 0) {
                progressRef.current.style.width = '100%';
                progressRef.current.style.height = '16px';
                progressRef.current.style.display = 'block';
                progressRef.current.style.minHeight = '16px';
            }
        }
    }, []);
    
    // Update this useEffect in your Progress component
    useEffect(() => {
        // Force all progress elements to have correct height
        if (progressRef.current) {
            const progressBar = progressRef.current;
            
            // Force bar height
            const barElement = progressBar.querySelector('.bar');
            if (barElement) {
                barElement.style.height = '4px';
                barElement.style.minHeight = '4px';
                barElement.style.top = '50%';
                barElement.style.transform = 'translateY(-50%)';
            }
            
            // Force buffer height
            const bufferElement = progressBar.querySelector('.buffer');
            if (bufferElement) {
                bufferElement.style.height = '4px';
                bufferElement.style.minHeight = '4px';
                bufferElement.style.top = '50%';
                bufferElement.style.transform = 'translateY(-50%)';
            }
            
            // Force subtitle heights
            const subtitleElement = progressBar.querySelector('.subtitle');
            if (subtitleElement) {
                subtitleElement.style.height = '4px';
                subtitleElement.style.minHeight = '4px';
                subtitleElement.style.top = '50%';
                subtitleElement.style.margin = '0';
                subtitleElement.style.transform = 'translateY(-50%)';
                subtitleElement.style.maxWidth = '100%';
                
                const spans = subtitleElement.querySelectorAll('span');
                spans.forEach(span => {
                    span.style.height = '4px';
                    span.style.minHeight = '4px';
                    span.style.top = '50%';
                    span.style.transform = 'translateY(-50%)';
                });
            }
        }
    }, [props.currentTime]); // Re-run when current time changes
    
    // Modify the return statement in the Progress component
    return (
        <div 
            ref={progressRef}
            className={`progress ${grabbing || isDraggingHandle ? 'grabbing' : ''}`} 
            onClick={onProgressClick}
            onMouseDown={onProgressMouseDown}
            onMouseMove={onMouseMove}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            style={{
                width: '100%', 
                height: '16px', 
                minHeight: '16px', 
                display: 'flex', 
                alignItems: 'center',
                position: 'relative',
                boxSizing: 'border-box',
                backgroundColor: 'rgba(50, 50, 50, 0.7)'
            }}
        >
            {/* Buffer indicator - thinner line */}
            <div 
                className="buffer" 
                style={{ 
                    width: `${bufferPercent}%`,
                    height: '4px', /* REDUCED for line appearance */
                    minHeight: '4px',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 6,
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    borderRadius: '2px'
                }}
            ></div>
            
            {/* Subtitle markers - thinner lines */}
            <div className="subtitle" style={{ 
                width: '100%', 
                height: '4px', /* REDUCED for line appearance */
                minHeight: '4px',
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                right: 0,
                zIndex: 5,
                maxWidth: '100%',
                marginTop: 0,
            }}>
                {props.subtitle.length <= 1000
                    ? props.subtitle.map((item, index) => {
                        const { duration } = props.player.element;
                        
                        // Skip rendering very small subtitles that would be barely visible
                        // This improves performance while keeping visual fidelity
                        const widthPercent = ((item.endTime - item.startTime) / duration) * 100;
                        if (widthPercent < 0.07) return null; // Skip tiny subtitles
                        
                        return (
                            <span
                                key={index}
                                className="item"
                                style={{
                                    left: `${(item.startTime / duration) * 100}%`,
                                    width: `${widthPercent}%`,
                                    height: '4px',
                                    minHeight: '4px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                    position: 'absolute',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    borderRadius: '2px',
                                    maxWidth: '100%',
                                    marginTop: 0
                                }}
                            ></span>
                        );
                    })
                    : // If more than 1000 subtitles, render a simplified representation
                    props.subtitle.length > 0 ? (
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: 'rgba(255,255,255,0.7)',
                            fontSize: '10px',
                            whiteSpace: 'nowrap'
                        }}>
                            {props.subtitle.length} subtitles (simplified view)
                        </div>
                    ) : null
                }
            </div>
            
            {/* Progress bar - thinner line */}
            <div 
                className="bar" 
                style={{ 
                    width: `${progressPercent}%`,
                    height: '4px', /* REDUCED for line appearance */
                    minHeight: '4px',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 7,
                    backgroundColor: 'rgba(255, 51, 51, 0.5)', 
                    boxShadow: '0 0 3px rgba(255, 51, 51, 0.5)',
                    borderRadius: '2px'
                }}
            >
                {/* Keep the handle the same size */}
                <div 
                    className="handle"
                    style={{
                        height: '16px',
                        width: '16px',
                        minHeight: '16px',
                        minWidth: '16px',
                        display: 'block',
                        position: 'absolute',
                        right: '-8px',
                        top: '50%',
                        transform: 'translate(0, -50%)',
                        backgroundColor: 'rgba(255, 51, 51, 0.7)', 
                        borderRadius: '50%',
                        border: '2px solid white',
                        boxShadow: '0 0 4px rgba(0, 0, 0, 0.5)',
                        zIndex: 10
                    }}
                ></div>
            </div>
            
            {/* Time preview remains the same */}
            {hoverTime !== null && (
                <div 
                    className="time-preview" 
                    style={{ 
                        left: hoverPosition,
                        opacity: 1,
                        zIndex: 15
                    }}
                >
                    {formatTime(hoverTime)}
                </div>
            )}
        </div>
    );
};

const Duration = (props) => {
    const getDuration = useCallback((time) => {
        time = time === Infinity ? 0 : time;
        return DT.d2t(time).split('.')[0];
    }, []);

    return (
        <div className="duration">
                {getDuration(props.currentTime)} / {getDuration(props.player.element.duration || 0)}
        </div>
    );
};

const FooterContainer = styled.div`
    position: relative;
    width: 100%;
    height: 200px; /* Increased from 180px */
    min-height: 200px; /* Increased from 150px */
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background-color: #1e1e1e;
    
    .combined-container {
        position: relative;
        flex: 1;
        width: 100%;
        overflow: hidden;
    }
    
    @media (max-width: 768px) {
        height: 170px; /* Increased from 130px */
        min-height: 150px; /* Increased from 120px */
    }
`;

export default function Footer(props) {
    const $footer = createRef();
    const [render, setRender] = useState({
        padding: 2,
        duration: 10,
        gridGap: 10,
        gridNum: 110,
        beginTime: -5,
    });
    const [waveform, setWaveform] = useState(null);
    const [scrollY, setScrollY] = useState(0);

    // Create a props object with waveform for child components
    const childProps = {
        ...props,
        waveform: waveform,
        setWaveform: setWaveform
    };
    
    const onScroll = useCallback(
        (event) => {
            console.log('event.target.scrollLeft', event.target.scrollLeft);
            const delta = event.target.scrollLeft - scrollY;
            const time = delta / 10;
            let currentTime = props.player.element.currentTime;
            if (currentTime + time < 0 || currentTime + time > props.player.element.duration) return;
            currentTime = props.player.element.currentTime + time;

            console.log('time', time);
            console.log('delta', delta);
            console.log('props.player.currentTime', currentTime);
            console.log('render.beginTime', render.beginTime);
            console.log('render.gridNum', render.gridNum);
            console.log('render.duration', render.duration);
            console.log('render.padding', render.padding);
            console.log('render.gridGap', render.gridGap);
            console.log('width', document.body.clientWidth);
            console.log('duration', props.player.element.duration);
            console.log(`Seeking to time - onScroll - 2: ${currentTime}`);
            props.player.updateTime(currentTime);

            setScrollY(event.target.scrollLeft);
        },
        [props.player, render, scrollY],
    );

    const getDuration = useCallback((time) => {
        time = time === Infinity ? 0 : time;
        return DT.d2t(time).split('.')[0];
    }, []);

    return (
        <FooterContainer className="footer" ref={$footer}>
            {props.player ? (
                <React.Fragment>
                    {/* Duration display */}
                    <div style={{ 
                        height: '15px', 
                        fontSize: '12px',
                        flexShrink: 0, 
                        position: 'relative', 
                        zIndex: 101,
                        justifyContent: 'center', 
                        display: 'flex', 
                        gap: 0,
                        margin: 0,
                        padding: 0,
                    }}>
                        {getDuration(props.currentTime)} / {getDuration(props.player.element.duration || 0)}
                    </div>

                    {/* Progress component container - simplified for YouTube style */}
                    <div style={{ 
                        height: '30px', /* Container for the progress bar */
                        flexShrink: 0, 
                        position: 'relative', 
                        zIndex: 100,
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 10px',
                        marginBottom: '5px',
                        width: '100%', /* Explicit width */
                        boxSizing: 'border-box', /* Ensure padding doesn't add to width */
                        border: '1px solid rgba(255, 255, 255, 0.1)', /* Visible border for debugging */
                    }}>
                        <Progress {...childProps} />
                    </div>

                    {/* Combined container for waveform, metronome, and timeline */}
                    <div className="combined-container" style={{ 
                        position: 'relative', 
                        flex: 1,
                        border: '1px solid rgba(100, 100, 100, 0.3)',
                        borderRadius: '3px',
                        overflow: 'hidden',
                        isolation: 'isolate' /* Create new stacking context */
                    }}>
                        {/* Waveform at the bottom layer */}
                        <div style={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            width: '100%', 
                            height: '100%', 
                            zIndex: 10,
                            pointerEvents: 'none'
                        }}>
                            <Waveform {...childProps} setRender={setRender} />
                        </div>
                        
                        {/* Timeline layer */}
                        <div style={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            width: '100%', 
                            height: '100%', 
                            zIndex: 30,
                            pointerEvents: 'none'
                        }}>
                            <Timeline {...childProps} render={render} />
                        </div>
                        
                        {/* Metronome on top to catch all clicks except for subtitle boxes */}
                        <div style={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            width: '100%', 
                            height: '100%', 
                            zIndex: 20, /* Below timeline boxes but above waveform */
                            pointerEvents: 'auto'
                        }}>
                            <Metronome {...childProps} render={render} />
                        </div>
                    </div>
                </React.Fragment>
            ) : null}
        </FooterContainer>
    );
}
